import { SITE_ORIGIN } from './site';
import manifest from './pageSeo.json';

/**
 * Per-page title, description and canonical for every route outside the content hub.
 *
 * Before this, ten public pages shared one title and one description from the shell,
 * so search results and shared links could not tell them apart, and no page on the
 * site had a canonical at all. Articles already carry their own from `articles.js`;
 * this is the same idea for everything else.
 *
 * The data lives in pageSeo.json because the prerenderer and the sitemap builder
 * need the same list and neither runs through a bundler. This module is the React
 * side of it: the derived canonical, and the shape `useDocumentHead` expects.
 */

export const PUBLIC_PAGES = manifest.pages;

/**
 * Routes that must never be indexed.
 *
 * `/share/:token` is the one that matters most: those URLs point at a family's
 * memorial video. Being unlinked is not the same as being unindexable, and a link
 * only has to be pasted somewhere public once. The rest are session-gated or
 * token-bearing and have no business in a search result either.
 *
 * `/g/*` is deliberately absent: it already carries an `X-Robots-Tag` response
 * header from vercel.json plus a meta tag from CampaignLandingPage.
 */
export const NOINDEX = 'noindex, nofollow';

/** The canonical URL for a page path, with no trailing slash except on the root. */
export const canonicalFor = (pagePath) =>
  (pagePath === '/' ? SITE_ORIGIN : `${SITE_ORIGIN}${pagePath}`);

export const seoFor = (key) => {
  const page = PUBLIC_PAGES[key];
  if (!page) throw new Error(`no SEO entry for page "${key}"`);
  return {
    title: page.title,
    description: page.description,
    canonical: canonicalFor(page.path),
    ogType: 'website',
  };
};

/** Head props for a route that must stay out of the index. */
export const privatePageSeo = (title) => ({
  title: title ? `${title} | Mixtape Memories` : 'Mixtape Memories',
  robots: NOINDEX,
});
